import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, BookOpen, Sparkles, Clock } from 'lucide-react';
import AboutBookModal from './AboutBookModal';
import { BACKEND_URL } from '@/config/api';

const NewTitlesDropdown = ({ 
  books, 
  sectionName = "New Titles",  // Dynamic: "New Titles" or "Recently Added"
  sectionVisible = true,       // If false, don't render this section
  onAddToLibrary, 
  onReadNow, 
  isExpanded = false, 
  onToggleExpand 
}) => {
  const [selectedBook, setSelectedBook] = useState(null);
  const [booksPerRow, setBooksPerRow] = useState(8);

  // Calculate books per row based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setBooksPerRow(4);
      } else if (window.innerWidth < 1024) {
        setBooksPerRow(6);
      } else {
        setBooksPerRow(8);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const displayedBooks = isExpanded ? books : books.slice(0, booksPerRow);
  const hasMore = books && books.length > booksPerRow;
  const remainingCount = books ? books.length - booksPerRow : 0;

  const toggleExpand = () => {
    if (onToggleExpand) {
      onToggleExpand();
    }
  };

  // Don't render if section should not be visible (no titles within 90 days)
  if (!sectionVisible) {
    return null;
  }

  // Choose icon based on section name
  const SectionIcon = sectionName === "Recently Added" ? Clock : Sparkles;
  const iconColor = sectionName === "Recently Added" ? "text-blue-400" : "text-amber-400";
  const accentColor = sectionName === "Recently Added" ? "text-blue-400 hover:text-blue-300" : "text-amber-400 hover:text-amber-300";
  const ringColor = sectionName === "Recently Added" ? "group-hover:ring-blue-500" : "group-hover:ring-amber-500";

  if (!books || books.length === 0) {
    return null; // Don't show empty section
  }

  return (
    <>
      <section className="rounded-2xl bg-slate-900/60 p-4 shadow-xl shadow-slate-950/50 ring-1 ring-slate-800">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <SectionIcon className={`w-5 h-5 ${iconColor}`} />
            <h2 className="text-base font-semibold sm:text-lg">{sectionName}</h2>
            <span className="text-xs text-slate-400 ml-2">
              {books.length} {books.length === 1 ? 'book' : 'books'}
            </span>
          </div>
          
          {hasMore && (
            <button
              onClick={toggleExpand}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-medium transition"
              data-testid="new-titles-expand-btn"
            >
              {isExpanded ? (
                <>
                  <span>Show Less</span>
                  <ChevronUp className="w-4 h-4" />
                </>
              ) : (
                <>
                  <span>+{remainingCount} More</span>
                  <ChevronDown className="w-4 h-4" />
                </>
              )}
            </button>
          )}
        </div>

        {/* Books Grid */}
        <div className="mt-4">
          <div 
            className={`grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-3 transition-all duration-300 ${
              isExpanded ? 'max-h-none' : 'max-h-[280px] overflow-hidden'
            }`}
          >
            {displayedBooks.map((book) => (
              <div
                key={book.id}
                onClick={() => setSelectedBook(book)}
                className="group cursor-pointer"
              >
                <div className={`aspect-[2/3] w-full overflow-hidden rounded-lg bg-slate-800 mb-1.5 ring-1 ring-slate-700 transition ${ringColor} group-hover:ring-2`}>
                  {book.cover_url ? (
                    <img
                      src={`${BACKEND_URL}${book.cover_url}`}
                      alt={book.title}
                      className="h-full w-full object-cover transition group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-slate-500">
                      <BookOpen className="h-8 w-8" />
                    </div>
                  )}
                </div>
                <h3 className="font-medium text-slate-50 text-[10px] sm:text-xs line-clamp-2 leading-tight mb-0.5">
                  {book.title}
                </h3>
                <p className="text-[9px] sm:text-[10px] text-slate-400 line-clamp-1">
                  {book.author}
                </p>
              </div>
            ))}
          </div>
          
          {/* Gradient fade when collapsed */}
          {!isExpanded && hasMore && (
            <div className="relative">
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-900/90 to-transparent pointer-events-none" />
              <button
                onClick={toggleExpand}
                className={`relative z-10 w-full mt-2 py-2 text-center text-sm ${accentColor} font-medium transition`}
              >
                +{remainingCount} more {sectionName.toLowerCase()}
              </button>
            </div>
          )}
        </div>
      </section>

      {selectedBook && (
        <AboutBookModal
          book={selectedBook}
          isOpen={!!selectedBook}
          onClose={() => setSelectedBook(null)}
          isAuthenticated={true}
          onReadNow={onReadNow}
          onAddToLibrary={onAddToLibrary}
        />
      )}
    </>
  );
};

export default NewTitlesDropdown;
